import { accountIsOrganization, accountIsUser, } from "@buildwithflux/models";
const testUserEmailAddresses = [
    /.+@e.rainforestqa.com$/,
    /.*cypress-test-user.*@flux.ai$/,
    "aptanatester@gmail.com",
    "fluxappcitestuser@defygravityinc.com",
];
export function isTestAccount(account, strategy = "legacy") {
    if (!account) {
        return false;
    }
    if ("defaultOrganization" in account) {
        return !!account.defaultOrganization?.isTest;
    }
    if (accountIsUser(account) && !!account.is_test) {
        return true;
    }
    if (accountIsOrganization(account) && !!account.isTest) {
        return true;
    }
    const email = account.email;
    if (email && isAutomatedTestEmailAddress(email)) {
        return true;
    }
    // We have removed matching here that previously returned true in all conditions if the user's handle contained
    // the word "test" anywhere
    // This is mainly why we don't like this test matching
    if ((strategy === "loose" || strategy === "legacy") && email && isEmployeeTestEmailAddress(email)) {
        return true;
    }
    // This one is also suspect (but at least it only looks for _standalone_ "test" in the name)
    if (strategy === "loose" && accountIsOrganization(account) && isTestDisplayName(account.displayName)) {
        return true;
    }
    return false;
}
/**
 * Generally, we should not be using this method at all (because it only checks handle, it's not very useful).
 * We should try to remove existing usages.
 *
 * Generally, use isSuspectedTestAccount() instead.
 *
 * This function is used in one place only:
 *  - The getProjectUsage method,
 */
export function isTestHandle(handle) {
    return (!!handle && (handle.startsWith("rf-user") || handle.startsWith("rf-edge-user") || handle.startsWith("rf_user")));
}
const hasStandaloneWord = (word) => (s) => {
    const pattern = new RegExp(`^(.*\\s)?${word}(\\s.*)?\$`, "i");
    return !!s.match(pattern);
};
function isTestDisplayName(name) {
    return !!name && hasStandaloneWord("test")(name);
}
const employeeEmailAddresses = [
    /.*test.*@flux.ai$/,
    /.*test.*@defygravityinc.com$/,
    /^brooks\.solveig\+.*test.*@gmail.com$/,
    /^chaykak2\+.*test.*@gmail.com$/,
    /^dominics\+.*test.*@gmail.com$/,
    /^jared\.kofron\+.*test.*@gmail.com$/,
    /^matthias.chills\+.*test.*@gmail.com$/,
    /.*test.*@oxyn.icu$/, // Jenner's external test domain
];
/**
 * Matches employee email addresses that are used for testing.
 *
 * This is used internally by isTestAccount(a, "loose")
 *
 * Our policy is:
 *  - You should usually use your @flux.ai email for testing (you can name those emails anything e.g. myname+test-101@flux.ai)
 *    - This should NOT usually change the behavior of the app: otherwise manual testing is really hard. So, you should
 *      try not to match against these email addresses at all.
 *  - Sometimes you need an external email address for testing purposes; in that case, you might want to add it to this
 *    list
 *
 * So, please use this sparingly, if at all. It makes testing difficult.
 *
 * When writing new features, it's better to match against testUserEmailAddresses - which are the genuinely automated
 * test email addresses. So, try to use isTestAccount(a, "strict") over isTestAccount(a, "loose")
 */
export function isEmployeeTestEmailAddress(email) {
    return (!!email &&
        employeeEmailAddresses.some((testEmail) => typeof testEmail === "string" ? testEmail === email : testEmail.test(email)));
}
/**
 * Matches just email addresses used by automated testing tools and in e2e/functional tests
 *
 * This is fine to use as much as you like. Try to retain the ability for e2e testing to use your feature though.
 */
export function isAutomatedTestEmailAddress(email) {
    return (!!email &&
        testUserEmailAddresses.some((testEmail) => typeof testEmail === "string" ? testEmail === email : testEmail.test(email)));
}
/**
 * Is the email address either an automated-testing email address or an employee email address?
 *
 * Places this is used:
 *  - Setting test_data to true on part search index entries if the organization email address matches this
 *  - Excluding users from user search index if the email address matches this
 *  - Not calling segment.trackEmailEvent() when the recipient email address matches this
 *
 * @deprecated Try to use isAutomatedTestEmailAddress() instead; it excludes employee emails (we should not be peforming
 *   special handling for employee email addresses; they should get the same treatment as any other email address). Use
 *   isEmployeeTestEmailAddress() in some limited cases. Or use isTestAccount(a, "strict") if you have the whole user.
 */
export function isTestEmailAddressIfWeSquintBadly(email) {
    return (!!email &&
        [...testUserEmailAddresses, ...employeeEmailAddresses].some((testEmail) => typeof testEmail === "string" ? testEmail === email : testEmail.test(email)));
}
//# sourceMappingURL=isTestAccount.js.map